import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  Edit as EditIcon,
  Money as MoneyIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  Policy,
  NoCrash,
  Speed,
  Person,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useToast } from "../../components/Toast";
import { Loading } from "../../components/Loading";
import BasicVehicleDetailsView, {
  VehicleProps,
} from "../../components/BasicVehicleDetailsView";
import NavigationHeader from "../../components/NavigationHeader";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { getValidToken } from "../../utils/authUtils";


const VehicleDetails: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [vehicle, setVehicle] = useState<VehicleProps | null>(null);
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get("new") === "true";
  const { planStatus } = usePlanStatus();
  useEffect(() => {
    fetchVehicleDetails();
  }, [vehicleId]);

  const fetchVehicleDetails = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.isSuccess && response.data.data) {
        // Handle the new response structure
        const vehicleData = response.data.data;
        
        // If outsourcedOwner is an array, take the first item
        if (Array.isArray(vehicleData.outsourcedOwner) && vehicleData.outsourcedOwner.length > 0) {
          vehicleData.outsourcedOwner = vehicleData.outsourcedOwner[0];
        }
        
        setVehicle(vehicleData);
      } else {
        setVehicle(null);
      }
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
      setVehicle(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/vehicles");
  };

  const VehicleCardData = [
    {
      title: "Finance",
      description: "Manage finance details",
      icon: <MoneyIcon sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/finance`,
      color: "#1976d2",
      canView: planStatus?.permissions?.canViewVehicleFinance,
    },
    {
      title: "Maintenance",
      description: "Manage maintenance details",
      icon: <BuildIcon sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/maintenance/${vehicle?.model}`,
      color: "#2196f3",
      canView: planStatus?.permissions?.canViewVehicleMaintenance,
    },
    {
      title: "Documents",
      description: "Manage documents",
      icon: <DescriptionIcon sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/documents`,
      color: "#00acc1",
      canView: planStatus?.permissions?.canViewVehicleDocument,
    },
    {
      title: "Insurance",
      description: "Manage insurance details",
      icon: <Policy sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/insurances`,
      color: "#00acc1",
      canView: planStatus?.permissions?.canViewVehicleInsurance,
    },
    {
      title: "Permits",
      description: "Manage permits details",
      icon: <NoCrash sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/permits`,
      color: "#00acc1",
      canView: planStatus?.permissions?.canViewVehiclePermit,
    },
    {
      title: "Odometer Readings",
      description: "View odometer readings",
      icon: <Speed sx={{ fontSize: 25 }} />,
      path: `/vehicles/${vehicleId}/odometer-readings`,
      color: "#00acc1",
      canView: planStatus?.permissions?.canViewVehicleOdometer,
    },
  ];

  const handleCardClick = (card: (typeof VehicleCardData)[0]) => {
    if (card.canView) {
      navigate(card.path);
    } else {
      showToast(
        `You don't have permission to view ${card.title} details`,
        "warning"
      );
    }
  };

  if (isLoading) {
    return <Loading message="Loading vehicle details..." fullScreen={true} />;
  }

  if (!vehicle) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4">Vehicle not found</Typography>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Vehicles
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {isNew && vehicle?.acquisitionType !== "Outsourced" && (
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          Welcome to your vehicle details page! Here you can:
          <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
            <li>Add and manage vehicle documents</li>
            <li>Track insurance policies and renewals</li>
            <li>Record PUC certificates</li>
            <li>Manage vehicle loans and EMI details</li>
            <li>Track maintenance records</li>
            <li>Manage permits and compliance documents</li>
          </ul>
          Click on any card below to get started!
        </Alert>
      )}

      <NavigationHeader
        title="Vehicle Details"
        breadcrumbs={[
          {
            label: "Vehicles",
            path: "/vehicles",
          },
          {
            label: "Vehicle Details",
          },
        ]}
        button={
          planStatus?.permissions?.canEditVehicle
            ? {
                label: "Edit Vehicle",
                icon: <EditIcon />,
                onClick: () =>
                  navigate(
                    `/vehicles/${vehicleId}/edit?outsourced=${
                      vehicle?.acquisitionType === "Outsourced"
                    }`
                  ),
              }
            : null
        }
      />

      {/* Basic Info Cards */}
      <BasicVehicleDetailsView
        vehicle={vehicle}
        title="Vehicle Details"
        small={true}
      />

      {vehicle?.acquisitionType === "Outsourced" && vehicle?.outsourcedOwner && (
        <OwnerDetails vehicle={vehicle.outsourcedOwner} small={true} />
      )}

      {vehicle?.acquisitionType !== "Outsourced" && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {VehicleCardData.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.path}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(card)}
                  sx={{ height: "100%" }}
                >
                  <CardContent
                    sx={{
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: "50%",
                        backgroundColor: `${card.color}15`,
                        color: card.color,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h2"
                      fontSize="0.9rem"
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.7rem"
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default VehicleDetails;

export const OwnerDetails = ({
  vehicle,
  small,
}: {
  vehicle: any; // Changed from OutsourcedOwner to any to handle the new structure
  small: boolean;
}) => {
  if (!vehicle) {
    return null;
  }
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Person sx={{ fontSize: small ? 25 : 32, color: "#000" }} />
        <Typography
          variant="h6"
          sx={{ fontSize: small ? "1rem" : "1.25rem", fontWeight: 600 }}
        >
          Owner Details
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Company Name
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.companyName || "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Owner Name
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.primaryContactName || "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            Owner Contact Number
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.primaryContactNumber || "Not Available"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontSize: small ? "0.75rem" : "0.875rem" }}
          >
            GST Percentage
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: small ? "0.875rem" : "1rem" }}
          >
            {vehicle?.gstPercentage || "-"}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
